<template>
  <div class="offcanvas offcanvas-end" id="OffcanvasTaxForm" ref="offcanvas" data-bs-backdrop="static">
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ $t('VAT Registration') }}
      <button type="button" style="font-size: small;" class="btn-close shadow-none text-reset" data-bs-dismiss="offcanvas" aria-label="Close" />
    </div>

    <div class="offcanvas-body" v-if="loading">
      <placeholder-content :is-load="true" />
    </div>
    <div class="offcanvas-body" v-else-if="!hasPermission">
      <access-denied/>
    </div>
    <div class="offcanvas-body p-0" v-else>
      <div class="border-bottom mb-3 px-3 py-2">
        <i class="fa-solid fa-circle-info me-2" />
        {{ $t('VAT registration belongs to the business not the branch.') }}
      </div>
      <div class="px-3">
        <div class="row g-3 py-2">
          <div class="col-12">
            <label class="form-label">
              {{ $t('Tax Policy') }} <span class="text-danger">*</span>
            </label>
            <select2
              v-model="v$.form.tax_policy.$model"
              :options="select2TaxPolicy.options"
              :settings="select2TaxPolicy.setting"
            />
            <div class="form-text">
              {{ taxPolicyHint }}
            </div>
          </div>

          <div class="col-12" v-if="isVatRegistered">
            <label for="shopVatRate" class="form-label">
              {{ $t('Vat Rate') + ' (%)' }} <span class="text-danger">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="shopVatRate"
                type="number"
                inputmode="decimal"
                v-model.trim="v$.form.vat_rate.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter vat') + '...'"
              >
              <div v-for="error of v$.form.vat_rate.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>
          </div>

          <!--
            ชื่อผู้ประกอบการ + เลขประจำตัวผู้เสียภาษี ต้องกรอกได้แม้ร้าน "ไม่จด VAT"
            เพราะใบเสร็จรับเงิน/บิลเงินสด ก็เป็น "ใบรับ" ตามมาตรา 105 ที่บังคับให้มี
            ชื่อผู้ประกอบการและเลขผู้เสียภาษี (ใช้เลขบัตรประชาชน/เลขทะเบียนนิติบุคคล)
            บังคับกรอกเฉพาะร้านที่จด VAT — ร้านไม่จดกรอกได้แต่ไม่บังคับ
            (silver-spoon-api/docs/vat-compliance.md §2)
          -->
          <div class="col-12">
            <label for="shopTaxEntityName" class="form-label">
              {{ $t('Registered Business Name') }} <span class="text-danger" v-if="isVatRegistered">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="shopTaxEntityName"
                type="text"
                v-model.trim="v$.form.tax_entity_name.$model"
                class="form-control shadow-none"
                :placeholder="$t('e.g. ABC Food Co.')"
              >
              <div class="form-text">
                {{ entityNameHint }}
              </div>
              <div v-for="error of v$.form.tax_entity_name.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>
          </div>

          <div class="col-12">
            <label for="shopTaxId" class="form-label">
              {{ $t('Tax ID') }} <span class="text-danger" v-if="isVatRegistered">*</span>
            </label>
            <div class="input-group-sm">
              <input
                id="shopTaxId"
                type="text"
                inputmode="numeric"
                maxlength="13"
                v-model.trim="v$.form.tax_id.$model"
                class="form-control shadow-none"
                :placeholder="$t('Enter tax id') + '...'"
              >
              <div class="form-text">
                {{ taxIdHint }}
              </div>
              <div v-for="error of v$.form.tax_id.$errors" class="d-block invalid-feedback" :key="error.$uid">
                {{ error.$message }}
              </div>
            </div>
          </div>

          <!-- รหัสสถานประกอบการอยู่ที่สาขา เพราะสรรพากรออกให้ต่อที่ตั้ง -->
          <div class="col-12" v-if="isVatRegistered">
            <div class="alert alert-light border py-2 mb-0 small">
              {{ $t('Each branch also needs its own branch code from the branch settings page.') }}
            </div>
          </div>

          <div class="col-12">
            <div class="text-muted" style="font-size: 12px;">
              <i class="fa-solid fa-circle-info me-1" />
              {{ $t('Applies to new bills and expenses only — documents already issued keep their own tax values.') }}
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="bg-light border-top justify-content-center modal-footer py-2" v-if="!loading && hasPermission">
      <ladda
        text="Save"
        :btn-class="'btn btn-primary btn-block '"
        :loading="loadingSave"
        icon="<i class='fa-solid fa-floppy-disk'></i>"
        :disabled="v$.$invalid"
        @click="handleEdit"
      />
      <button type="button" data-bs-dismiss="offcanvas" class="border-dark-subtle btn-sm btn btn-light me-2 ms-2">
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
/**
 * ตั้งค่าการจด VAT ของ "ร้าน" (นิติบุคคล) — ไม่ใช่ของสาขา
 *
 * ภายใต้เลขประจำตัวผู้เสียภาษีเดียวกัน จะมีสาขาหนึ่งจด VAT อีกสาขาไม่จด ไม่ได้
 * ค่าพวกนี้จึงอยู่ที่ /v1/settings ที่เดียว แล้วสาขาเหลือแค่รหัสสถานประกอบการ (branch_no)
 * ดู silver-spoon-api/docs/vat-compliance.md §2
 *
 * ทั้งบล็อกต้องส่งพร้อมกัน (API บังคับ) — กันสถานะครึ่งๆ เช่นเปิด VAT แต่ยังไม่มีเลขผู้เสียภาษี
 */
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import AccessDenied from '@/plugins/AccessDenied.vue';
import Ladda from '@/plugins/Ladda.vue';
import Select2 from 'vue3-select2-component';
import { useVuelidate } from '@vuelidate/core';
import validation from './assets/template/validation-tax';
import { Setting } from '@/services/endpoint/setting.service';

export default {
  setup: () => ({ v$: useVuelidate() }),
  validationConfig: { $lazy: true },
  validations() {
    return validation(this.form);
  },
  components: { PlaceholderContent, AccessDenied, Ladda, Select2 },
  data() {
    return {
      loading: true,
      hasPermission: false,
      loadingSave: false,
      select2TaxPolicy: {
        options: [],
        setting: {
          minimumResultsForSearch: Infinity,
        }
      },
      form: {
        tax_policy: 'no_tax',
        vat_rate: '',
        tax_id: '',
        tax_entity_name: '',
      },
    };
  },
  computed: {
    isVatRegistered() {
      return !!this.form.tax_policy && this.form.tax_policy !== 'no_tax';
    },
    // included/excluded ต่างกันที่ "ราคาเมนูที่ตั้งไว้รวม VAT แล้วหรือยัง" ไม่ใช่เรื่องพิมพ์ใบเสร็จ
    taxPolicyHint() {
      const map = {
        included: this.$t('Menu prices already include VAT — VAT is extracted from the price.'),
        excluded: this.$t('VAT is added on top of the menu price.'),
        no_tax: this.$t('This shop is not VAT-registered — receipts are not tax invoices.'),
      };
      return map[this.form.tax_policy] || map.no_tax;
    },
    // ร้านจด VAT ใช้ชื่อ/เลขตามทะเบียน VAT · ร้านไม่จดใช้ชื่อผู้ประกอบการกับเลขบัตรประชาชน
    // ทั้งสองแบบพิมพ์ลงหัวใบเสร็จเหมือนกัน ต่างกันแค่ที่มาของเลข จึงต้องอธิบายให้ตรงกรณี
    entityNameHint() {
      return this.isVatRegistered
        ? this.$t('The name on your VAT registration — printed on tax invoices instead of the brand name.')
        : this.$t('The business owner name printed on the receipt instead of the brand name.');
    },
    taxIdHint() {
      return this.isVatRegistered
        ? this.$t('13 digits from your VAT registration.')
        : this.$t('13 digits — national ID for a sole proprietor or the juristic registration number.');
    },
  },
  methods: {
    async getData() {
      await Setting.get().then(res => {
        const d = res.data?.data ?? res.data ?? {};
        this.form = {
          tax_policy: d.tax_policy || 'no_tax',
          vat_rate: d.vat_rate ?? '',
          tax_id: d.tax_id || '',
          tax_entity_name: d.tax_entity_name || '',
        };
        this.select2TaxPolicy.options = (res.data?.filter?.tax_policy || []).map(item => ({
          id: item.value,
          text: this.$t(item.text)
        }));
        this.hasPermission = true;
      }).catch(err => {
        if (err?.response?.status === 403) this.hasPermission = false;
        throw err;
      });
    },
    async handleEdit() {
      this.loadingSave = true;
      this.v$.$touch();
      if (this.v$.$invalid) { this.loadingSave = false; return; }

      // ส่ง payload ตรงๆ ไม่ clone จาก response — API ปฏิเสธ key ที่ไม่รู้จัก
      // ชื่อ/เลขผู้เสียภาษีส่งเสมอ (ไม่ใช่ค่าของนโยบายภาษี แต่เป็นตัวตนของผู้ประกอบการ
      // ที่ต้องพิมพ์บนใบเสร็จรับเงินของร้านไม่จด VAT ด้วย) — ปล่อยว่างได้ API รับ nullable
      const params = {
        tax_policy: this.form.tax_policy,
        vat_rate: this.isVatRegistered ? Number(this.form.vat_rate) : 0,
        tax_id: this.form.tax_id,
        tax_entity_name: this.form.tax_entity_name,
      };

      await Setting.update(params).then(() => {
        this.$parent.fetchData();
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('VAT settings updated'));
        $(this.$refs.offcanvas).offcanvas('hide');
        this.loadingSave = false;
      }).catch(err => {
        if (err?.response?.data?.message) this.$toast.warn(err?.response?.data?.message);
        this.loadingSave = false;
      });
    },
  },
  async mounted() {
    $(this.$refs.offcanvas).offcanvas('show');
    $(this.$refs.offcanvas).on('shown.bs.offcanvas', async () => {
      this.loading = true;
      try {
        await this.getData();
        this.loading = false;
      } catch (err) {
        this.loading = false;
      }
    });
    $(this.$refs.offcanvas).on('hidden.bs.offcanvas', () => {
      this.$parent.offcanvasTaxForm.open = false;
    });
  }
};
</script>
